import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DialogDefaultV2 } from '../../../components/v2';
import {
  getTableV2DetailBodyCellSx,
  getTableV2HeaderCellSx,
  tableV2TableSx,
} from '../../../components/v2/tableV2Styles';
import TableService from '../../../services/TableService';
import CalendarioCiclo from './CalendarioCiclo';
import {
  URL_DATE_FORMAT,
  buildApiBody,
  calendarioFromFilterRows,
  explodeFilterRows,
  formatPeriodLabel,
  labelSeveridade,
} from './constants';

const PAGE_SIZE = 50;
const ROW_HEIGHT = 44;

const corSeveridade = (severidade) => {
  if (severidade === 'ALTA') return 'error';
  if (severidade === 'MEDIA') return 'warning';
  return 'default';
};

const problemasDoRecorte = (rows, diaFiltro) => explodeFilterRows(rows)
  .filter((linha) => !diaFiltro || linha.dia === diaFiltro)
  .sort((a, b) => String(a.dia || '').localeCompare(String(b.dia || ''))
    || String(a.tipo || '').localeCompare(String(b.tipo || ''), 'pt-BR'));

const CalendarioPessoaDialog = ({
  pessoa,
  inicio,
  fim,
  diaSelecionado,
  onClose,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [diaFiltro, setDiaFiltro] = useState(null);

  useEffect(() => {
    setDiaFiltro(diaSelecionado || null);
  }, [diaSelecionado, pessoa]);

  useEffect(() => {
    if (!pessoa?.usuarioId) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      setRows([]);
      try {
        const all = [];
        let page = 0;
        let total = Infinity;
        while (all.length < total && page < 20) {
          const response = await TableService.request(
            `reports/conformidade-inspecao/filter?p=${page}&s=${PAGE_SIZE}`,
            buildApiBody(inicio, fim, {
              usuarioIds: [pessoa.usuarioId],
              page,
              size: PAGE_SIZE,
            }),
          );
          const pageRows = response?.data?.rows || [];
          total = Number(response?.data?.totalRows) || pageRows.length;
          all.push(...pageRows);
          if (!pageRows.length) break;
          page += 1;
        }
        if (!cancelled) setRows(all);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [fim, inicio, pessoa]);

  const dias = useMemo(() => calendarioFromFilterRows(rows), [rows]);
  const problemas = useMemo(
    () => problemasDoRecorte(rows, diaFiltro),
    [diaFiltro, rows],
  );

  const titulo = pessoa
    ? `${pessoa.usuarioNome} · ciclo ${formatPeriodLabel(inicio, fim)}`
    : 'Calendário';
  const diaLabel = diaFiltro
    ? moment(diaFiltro, URL_DATE_FORMAT).format('DD/MM/YYYY')
    : null;

  return (
    <DialogDefaultV2
      open={Boolean(pessoa)}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title={titulo}
      readOnly
      maxWidth="xl"
      fullWidth
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error">Não foi possível carregar o calendário desta pessoa.</Alert>}
      {pessoa && !error && diaLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {`Dia ${diaLabel} em destaque. Os outros dias do ciclo estão visíveis só como contexto.`}
        </Typography>
      )}
      {pessoa && !error && (
        <CalendarioCiclo
          inicio={inicio}
          fim={fim}
          dias={dias}
          selectedKey={diaFiltro}
          onSelect={setDiaFiltro}
          pessoal
          atenuarDemais
        />
      )}
      {pessoa && !error && !loading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {diaLabel ? `Problemas de ${diaLabel}` : 'Problemas do ciclo'}
          </Typography>
          {!problemas.length && (
            <Typography variant="body2" color="text.secondary">
              {diaLabel ? 'Nenhum problema neste dia.' : 'Nenhum problema neste ciclo.'}
            </Typography>
          )}
          {problemas.length > 0 && (
            <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="medium" aria-label="Problemas da pessoa" sx={tableV2TableSx}>
                <TableHead>
                  <TableRow hover={false}>
                    <TableCell sx={getTableV2HeaderCellSx(ROW_HEIGHT)}>Dia</TableCell>
                    <TableCell sx={getTableV2HeaderCellSx(ROW_HEIGHT)}>Classificação</TableCell>
                    <TableCell sx={getTableV2HeaderCellSx(ROW_HEIGHT)}>Severidade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {problemas.map((linha, rowIndex) => {
                    const cellSx = getTableV2DetailBodyCellSx(ROW_HEIGHT, rowIndex);
                    return (
                      <TableRow key={`${linha.dia}-${linha.codigo}-${linha.severidade || 'sem'}-${rowIndex}`} hover>
                        <TableCell sx={cellSx}>
                          {linha.dia ? moment(linha.dia, URL_DATE_FORMAT).format('DD/MM/YYYY') : '—'}
                        </TableCell>
                        <TableCell sx={cellSx}>{linha.tipo}</TableCell>
                        <TableCell sx={cellSx}>
                          <Chip
                            size="small"
                            label={labelSeveridade(linha.severidade) || '—'}
                            color={corSeveridade(linha.severidade)}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}
    </DialogDefaultV2>
  );
};

CalendarioPessoaDialog.propTypes = {
  pessoa: PropTypes.object,
  inicio: PropTypes.number,
  fim: PropTypes.number,
  diaSelecionado: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default CalendarioPessoaDialog;
